const keyEventListenerFunctions = new WeakMap();
const blurEventListenerFunctions = new WeakMap();

function checkMenuFocusLost(trigger, container, closeDropdownMenu) {
  // If the user tabs out of the dropdown menu, close it. This follows the behaviour
  // outlined in https://www.w3.org/WAI/tutorials/menus/flyout/#use-parent-as-toggle
  //
  // Wait for the focus to shift to the next element
  setTimeout(
    () => {
      // Once the focus has shifted, check if the newly focused element is still within
      // the dropdown or is on the trigger element. If it is not, close the dropdown.
      if (
        !container.contains(document.activeElement) &&
        document.activeElement !== trigger
      ) {
        closeDropdownMenu(trigger);
      }
    },
    // Set the timeout to run after 50ms which should be enough time for the browser to
    // process the focus change. Ideally we'd use Vue's nextTick() here, but this isn't
    // used from Vue.
    50,
  );
}

/**
 * Configures keyboard event handlers for a popups trigger element and the focusable
 * elements within the popup container.
 *
 * The event handlers are designed to close the popup when the escape key is pressed, or
 * when the user tabs out of the popup or trigger. This assumes that the popup
 * focusable elements follow the trigger element in the tab order.
 *
 * Note that this function does not handle the opening of the popup.
 *
 * To disconnect the event handlers, call `disconnectKeyboardNavigation(trigger,
 * container)`.
 *
 * @param {Element} trigger
 * @param {Element} container
 * @param {Function} closeContainer A function that will close the container when
 * called. It is passed the trigger and container elements as arguments.
 */
export function configurePopupKeyboardNavigation(trigger, container, closeContainer) {
  const keyHandler = (event) => {
    if (closeContainer && (event.key === "Escape" || event.keyCode === 27)) {
      // Close the dropdown menu if 'Escape' key is pressed
      event.preventDefault();
      closeContainer(trigger);
      trigger.focus();
    } else if (closeContainer && (event.key === "Tab" || event.keyCode === 9)) {
      checkMenuFocusLost(trigger, container, closeContainer);
    }
  };
  const blurHandler = () => checkMenuFocusLost(trigger, container, closeContainer);

  // Add the event handlers to the maps so we can remove them later if needed
  keyEventListenerFunctions.set(trigger, keyHandler);
  blurEventListenerFunctions.set(trigger, blurHandler);

  trigger.addEventListener("keydown", keyHandler);
  container.addEventListener("keydown", keyHandler);
  // Native audio/video players don't bubble key events from within themselves. This
  // breaks our automatic closing of the menu if the media element is the last focusable
  // element in the popup. Unfortunately there is nothing we can do about catching "esc"
  // key presses when the media element has focus, but we can at least restore the
  // correct popup close behaviour (on tab out) by listening for the blur event on the
  // media elements.
  container
    .querySelectorAll("audio, video")
    .forEach((mediaElement) => mediaElement.addEventListener("blur", blurHandler));
}

/**
 * Disconnects the keyboard navigation event handlers for the popup trigger and
 * container.
 *
 * See `configurePopupTabKeyboardNavigation` for more details.
 *
 * @param {Element} trigger The popup trigger element.
 * @param {Element} container The popup container element.
 */
export function disconnectPopupKeyboardNavigation(trigger, container) {
  const keyHandler = keyEventListenerFunctions.get(trigger);
  const blurHandler = blurEventListenerFunctions.get(trigger);

  if (keyHandler) {
    trigger.removeEventListener("keydown", keyHandler);
    container.removeEventListener("keydown", keyHandler);
    keyEventListenerFunctions.delete(trigger);
  }
  if (blurHandler) {
    container
      .querySelectorAll("audio, video")
      .forEach((mediaElement) => mediaElement.removeEventListener("blur", blurHandler));
    blurEventListenerFunctions.delete(trigger);
  }
}
